import type { PrototypeVersion, UploadVersionResult } from '@atlas/api-contract';
import type { AxiosProgressEvent } from 'axios';

import { apiClient } from './client';

/**
 * `POST /v1/prototypes/:id/versions` — the one multipart endpoint in the app.
 *
 * Three things make this different from every other request module here, and
 * all three are the reason it lives in its own file rather than beside the JSON
 * calls:
 *
 * - **It is slow.** A 50 MB archive on a hotel connection takes minutes, and the
 *   shared client's 30-second timeout would kill it mid-flight, so this request
 *   opts out of the timeout entirely and relies on cancellation instead.
 * - **It reports progress.** `onUploadProgress` is the only honest source for a
 *   determinate bar; anything else would be an animation pretending to be one.
 * - **It is cancellable by the user.** The caller owns an `AbortController` and
 *   an abort is a normal outcome, not a failure to report.
 *
 * The two reads the viewer needs sit alongside it, because they address the
 * same resource and splitting one endpoint family across two modules would be
 * worse than the mild inconsistency of a multipart call sharing a file with two
 * plain GETs.
 */

/** Request options every read accepts, so React Query can cancel it. */
interface ReadOptions {
  signal?: AbortSignal | undefined;
}

/**
 * `signal` is spread conditionally rather than passed as `undefined`: axios
 * declares it without `| undefined`, and `exactOptionalPropertyTypes` is on.
 */
const withSignal = ({ signal }: ReadOptions) =>
  signal === undefined ? {} : { signal };

/**
 * A version as the version drawer lists it.
 *
 * A strict superset of the contract's `PrototypeVersion` — every field is
 * present and unchanged — plus the `threadCount` the drawer renders beside each
 * entry (spec R3.3). It is declared here rather than in `@atlas/api-contract`
 * because the count is a property of *this listing*, not of a version:
 * `GET /v1/versions/:id` returns a plain `PrototypeVersion`, and a field only
 * some responses populate would be a lie in the shared type. The API declares
 * the same superset on its side for the same reason.
 */
export interface PrototypeVersionSummary extends PrototypeVersion {
  /** Threads pinned to this version, resolved ones included. */
  threadCount: number;
}

/**
 * `GET /v1/prototypes/:id/versions` — every version, newest first, unpaginated.
 *
 * A prototype with no versions and an id that matches no prototype both answer
 * `200` with an empty array. Telling those apart is `getPrototype`'s job, and
 * the viewer route asks it in parallel with this.
 */
export const listVersions = async (
  prototypeId: string,
  options: ReadOptions = {},
): Promise<PrototypeVersionSummary[]> => {
  const { data } = await apiClient.get<PrototypeVersionSummary[]>(
    `/prototypes/${encodeURIComponent(prototypeId)}/versions`,
    withSignal(options),
  );

  return data;
};

/**
 * `GET /v1/versions/:id` — one version, including the `entryPath` the file
 * server resolves for the bare version URL and the `screenPaths` behind the
 * screen dropdown. 404 when the id is unknown.
 */
export const getVersion = async (
  versionId: string,
  options: ReadOptions = {},
): Promise<PrototypeVersion> => {
  const { data } = await apiClient.get<PrototypeVersion>(
    `/versions/${encodeURIComponent(versionId)}`,
    withSignal(options),
  );

  return data;
};

/** How much of the archive has reached the server. */
export interface UploadProgress {
  /** Bytes sent so far. */
  loaded: number;
  /** Total bytes to send, or `null` when the browser will not say. */
  total: number | null;
  /** 0–100, or `null` when the total is unknown and no bar can be drawn. */
  percent: number | null;
}

/** Everything the endpoint needs, already trimmed. */
export interface UploadVersionRequest {
  prototypeId: string;
  /** The ZIP itself. Travels as the `file` part. */
  file: File;
  /** Trimmed notes, or `null` when the box was left empty. */
  notes: string | null;
}

export interface UploadVersionOptions {
  /** Aborts the request. An abort surfaces as an `ApiError` of kind `canceled`. */
  signal?: AbortSignal | undefined;
  onProgress?: ((progress: UploadProgress) => void) | undefined;
}

/**
 * No timeout would leave a dead connection spinning forever; the shared 30
 * seconds would kill a legitimate upload. Ten minutes is past any archive that
 * fits under `UPLOAD_MAX_BYTES` on a connection worth waiting for.
 */
const UPLOAD_TIMEOUT_MS = 10 * 60_000;

const toProgress = (event: AxiosProgressEvent): UploadProgress => {
  const total = typeof event.total === 'number' && event.total > 0 ? event.total : null;

  return {
    loaded: event.loaded,
    total,
    percent: total === null ? null : Math.min(100, Math.round((event.loaded / total) * 100)),
  };
};

/**
 * Uploads an archive and returns the created version with its `droppedFiles`
 * report (spec R1.10).
 *
 * The empty `notes` field is omitted rather than sent blank: the DTO folds `''`
 * to `undefined` anyway, and not sending it keeps the request honest about what
 * the user actually filled in.
 */
export const uploadVersion = async (
  { prototypeId, file, notes }: UploadVersionRequest,
  { signal, onProgress }: UploadVersionOptions = {},
): Promise<UploadVersionResult> => {
  const body = new FormData();

  body.append('file', file, file.name);

  if (notes !== null && notes !== '') {
    body.append('notes', notes);
  }

  const { data } = await apiClient.post<UploadVersionResult>(
    `/prototypes/${encodeURIComponent(prototypeId)}/versions`,
    body,
    {
      timeout: UPLOAD_TIMEOUT_MS,
      /*
       * Deliberately not setting `Content-Type`: the browser has to write the
       * multipart boundary into it, and any value set here would replace the
       * one it generates, leaving the server unable to split the parts.
       */
      ...(signal === undefined ? {} : { signal }),
      ...(onProgress === undefined
        ? {}
        : {
            onUploadProgress: (event: AxiosProgressEvent) => {
              onProgress(toProgress(event));
            },
          }),
    },
  );

  return data;
};
